/* eslint-disable react/prop-types */
import React, {useEffect, useRef} from 'react';
import {useDrag, useDrop, DragPreviewImage} from 'react-dnd';
import {getEmptyImage} from 'react-dnd-html5-backend';

import {mergeRefs} from './refHelper';

export function draggable(WrappedComponent) {
  return React.forwardRef(
    (
      {
        itemID,
        itemType,
        disableDrag,
        onDrag,
        onDrop = () => {},
        disablePreview = false,
        onDragEnd = () => {},
        ...props
      },
      ref,
    ) => {
      const [{isDragging, canDrag}, drag, preview] = useDrag({
        item: {ID: itemID, type: itemType},
        canDrag: () => !disableDrag,
        begin: () => onDrag && onDrag(itemID, itemType),
        collect: (monitor) => ({
          isDragging: itemID === monitor.getItem()?.ID && itemType === monitor.getItemType(),
          canDrag: monitor.canDrag(),
        }),
        end: (dropResult, monitor) => {
          const item = monitor.getItem();
          const didDrop = monitor.didDrop();
          if (!didDrop) {
            onDrop(item, itemID, false);
          }
          if (onDragEnd && typeof onDragEnd === 'function') onDragEnd(item);
        },
      });

      useEffect(() => {
        if (disablePreview) {
          preview(getEmptyImage(), {captureDraggingState: true});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      return (
        <>
          <DragPreviewImage connect={preview} src={props.thumbnail} />
          <WrappedComponent
            itemID={itemID}
            drag={drag}
            // dragPreview={preview}
            isDragging={isDragging}
            canDrag={canDrag}
            onDrop={onDrop}
            ref={ref}
            disableDrag={disableDrag}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        </>
      );
    },
  );
}

export function droppable(WrappedComponent) {
  return React.forwardRef(
    (
      {
        itemID,
        acceptedItems,
        isDisableDrop = () => {},
        onHover = null,
        directOnHover = null,
        onDrop = () => {},
        ...props
      },
      ref,
    ) => {
      const containerRef = useRef(null);
      const [{isOver, canDrop}, drop] = useDrop({
        accept: acceptedItems,
        drop: (item, monitor) => {
          const clientOffset = monitor.getClientOffset();
          const containerBoundingRect = containerRef.current?.getBoundingClientRect();
          onDrop(item, itemID, true, clientOffset, containerBoundingRect);
        },
        hover(item, monitor) {
          if (!containerRef.current) {
            return;
          }

          if (directOnHover) {
            const currentclientOffset = monitor.getClientOffset();
            const containerBoundingRect = containerRef.current?.getBoundingClientRect();
            directOnHover(item, itemID, currentclientOffset, containerBoundingRect);
            return;
          }

          if (!onHover) return;

          const dragIndex = item.ID;
          const hoverIndex = itemID;
          // Don't replace items with themselves
          if (dragIndex === hoverIndex) {
            return;
          }
          // Determine rectangle on screen
          const hoverBoundingRect = containerRef.current?.getBoundingClientRect();
          // Get vertical middle
          const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
          // Determine mouse position
          const clientOffset = monitor.getClientOffset();
          // Get pixels to the top
          const hoverClientY = clientOffset.y - hoverBoundingRect.top;
          // Only perform the move when the mouse has crossed half of the items height
          // When dragging downwards, only move when the cursor is below 50%
          // When dragging upwards, only move when the cursor is above 50%
          // Dragging downwards
          if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
            return;
          }
          // Dragging upwards
          if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
            return;
          }
          // Time to actually perform the action
          onHover(item, itemID);
          // Note: we're mutating the monitor item here!
          // Generally it's better to avoid mutations,
          // but it's good here for the sake of performance
          // to avoid expensive index searches.
          item.ID = hoverIndex;
        },
        canDrop: (item) => !isDisableDrop(item),
        collect: (monitor) => ({
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
        }),
      });

      drop(containerRef);
      return (
        <WrappedComponent
          drop={containerRef}
          isOver={isOver}
          canDrop={canDrop}
          ref={mergeRefs(ref, containerRef)}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      );
    },
  );
}
